import { Component, OnInit, OnDestroy } from '@angular/core';
import { DashboardResponse } from 'src/app/services/utility.service';
import { DashboardDataService } from 'src/app/services/dashboard-data.service';
import { DateTime } from "luxon";
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BidType } from 'src/app/models/api-response.models';
import { FormatHelperService } from 'src/app/services/format-helper.service';

@Component({
  selector: 'app-homepage',
  templateUrl: './homepage.component.html',
  styleUrls: ['./homepage.component.scss']
})
export class HomepageComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  currentDate = new Date();
  displayDate = '';

  topStats = {
    totalParticipants: null as number | null,
    totalEnergyTraded: null as number | null,
    totalCbdcTransaction: null as number | null
  };

  participantStats = {
    buyers: null as number | null,
    sellers: null as number | null
  };

  activeParticipantStats = {
    preferential: { buyer: null as number | null, seller: null as number | null },
    dynamic: { buyer: null as number | null, seller: null as number | null }
  };

  loading = false;
  error = "";
  showDashesForToday = false;

  today = "";
  selectedDate = "";
  maxDate: string = "";
  utilityId: string | null = null;

  constructor(
    private dashboardData: DashboardDataService,
    private localStorageService: LocalStorageService,
    private formatHelper: FormatHelperService
  ) { }

  ngOnInit(): void {
    this.utilityId = this.localStorageService.getUserId();
    if (!this.utilityId && !this.dashboardData.isPartnerSession()) {
      this.error = "Utility ID not found.";
      return;
    }
    const todayISO = DateTime.local().toISODate();
    this.today = todayISO ?? "";
    this.selectedDate = this.today;
    this.maxDate = todayISO ?? "";
    this.updateDisplayDate();
    this.loadForSelectedDate();
  }

  loadForSelectedDate() {
    if (!this.utilityId && !this.dashboardData.isPartnerSession()) {
      this.error = "Utility ID not found.";
      return;
    }

    this.error = "";
    this.loading = true;

    // Cancel any in-flight requests from a previous date change
    this.destroy$.next();

    // Reset active participant stats before new fetch
    this.activeParticipantStats = {
      preferential: { buyer: null, seller: null },
      dynamic: { buyer: null, seller: null }
    };

    const startDate = this.selectedDate;
    const endDate = this.selectedDate;

    // 4 parallel calls: global dashboard + preferential + daily + monthly
    forkJoin({
      dashboard: this.dashboardData.getDashboard(),
      preferential: this.dashboardData.getStatistics(startDate, endDate, BidType.Preferential),
      daily: this.dashboardData.getStatistics(startDate, endDate, BidType.Daily),
      monthly: this.dashboardData.getStatistics(startDate, endDate, BidType.Monthly)
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          console.log('[Homepage] Parallel API Responses:', results);
          
          // 1. GLOBAL Totals
          const dash = results.dashboard;
          this.topStats.totalCbdcTransaction = dash.total_cbdc_transactions;
          this.topStats.totalEnergyTraded = dash.total_traded_value;
          this.topStats.totalParticipants = dash.total_number_of_users;
          this.participantStats.buyers = dash.total_number_of_consumers;
          this.participantStats.sellers = dash.total_number_of_prosumers;

          // 2. Preferential
          if (results.preferential?.participation_statistics) {
            const part = results.preferential.participation_statistics;
            this.activeParticipantStats.preferential.buyer = part.buyers ?? 0;
            this.activeParticipantStats.preferential.seller = part.sellers ?? 0;
          }

          // 3 & 4. Dynamic (Daily + Monthly)
          let dynamicBuyer = 0;
          let dynamicSeller = 0;
          
          if (results.daily?.participation_statistics) {
            dynamicBuyer += results.daily.participation_statistics.buyers ?? 0;
            dynamicSeller += results.daily.participation_statistics.sellers ?? 0;
          }
          if (results.monthly?.participation_statistics) {
            dynamicBuyer += results.monthly.participation_statistics.buyers ?? 0;
            dynamicSeller += results.monthly.participation_statistics.sellers ?? 0;
          }
          
          this.activeParticipantStats.dynamic.buyer = dynamicBuyer;
          this.activeParticipantStats.dynamic.seller = dynamicSeller;
        },
        error: (err) => {
          this.handleError(err);
        },
        complete: () => {
          this.loading = false;
          this.checkTodayDashes();
        }
      });
  }

  private checkTodayDashes() {
    if (this.isToday) {
      // Include ALL stats — top-level AND active participants (preferential + dynamic)
      // so that real dynamic data is never hidden by zero global stats
      const vals = [
        this.topStats.totalCbdcTransaction,
        this.topStats.totalEnergyTraded,
        this.topStats.totalParticipants,
        this.participantStats.buyers,
        this.participantStats.sellers,
        this.activeParticipantStats.preferential.buyer,
        this.activeParticipantStats.preferential.seller,
        this.activeParticipantStats.dynamic.buyer,
        this.activeParticipantStats.dynamic.seller
      ];
      const anyVal = vals.some(v => v !== null && v !== undefined && Number(v) > 0);
      this.showDashesForToday = !anyVal;
    } else {
      this.showDashesForToday = false;
    }
  }

  get isToday(): boolean {
    return this.selectedDate === this.today;
  }

  private handleError(err: any) {
    console.error('API Error:', err);
    this.error = err?.error?.message || err?.error?.detail || err.message || "Failed to load data";
    this.loading = false;
  }

  display(value: number | string | undefined | null): string {
    return this.formatHelper.display(value, this.isToday, this.showDashesForToday);
  }

  prevDate() {
    this.currentDate.setDate(this.currentDate.getDate() - 1);
    this.updateDisplayDate();
    this.syncSelectedDate();
    this.loadForSelectedDate();
  }

  nextDate() {
    if (this.selectedDate >= this.today) return;
    
    this.currentDate.setDate(this.currentDate.getDate() + 1);
    this.updateDisplayDate();
    this.syncSelectedDate();
    this.loadForSelectedDate();
  }

  onDateChange(event: any) {
    if (event.value) {
      console.log('Date changed to:', event.value);
      
      let dt = DateTime.fromJSDate(event.value);
      const todayDt = DateTime.fromISO(this.today);
      
      // Feature: Restrict future dates
      if (dt > todayDt) {
        dt = todayDt;
      }

      if (dt.isValid) {
        this.currentDate = dt.toJSDate();
        this.updateDisplayDate();
        this.syncSelectedDate();
        this.loadForSelectedDate();
      }
    }
  }

  private syncSelectedDate() {
    this.selectedDate = DateTime.fromJSDate(this.currentDate).toISODate() ?? "";
    console.log('Synchronized selectedDate:', this.selectedDate);
  }

  private updateDisplayDate() {
    this.displayDate = DateTime.fromJSDate(this.currentDate).toFormat('d MMMM yyyy');
    console.log('Updated displayDate:', this.displayDate);
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
